import { CircleAlertIcon, Typography } from "@codat/orchard-ui";
import { useDisplayTextQuery } from "api/company";
import classNames from "classnames";
import { isOptional } from "contexts/config-fn";
import { ConfigStatusType, useConfig } from "contexts/ConfigContext";
import { SourceTypeStatus } from "contexts/SourceTypesContext";
import useContinueRedirectUrl from "hooks/useContinueRedirectUrl";
import { useCreateConnectionClickHandler } from "hooks/useCreateConnectionClickHandler";
import useFilteredQueryString from "hooks/useFilteredQueryString";
import useNextSourceTypeRedirectHandler from "hooks/useNextSourceTypeRedirectHandler";
import { useSourceTypeDisplayProps } from "hooks/useSourceTypeDisplayProps";
import { useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { formatUrl, usePageViewedEvent } from "utils";

import Button from "components/Button";
import ConnectionComplete from "components/ConnectionComplete";
import LinkPageRoot from "components/LinkPageRoot";
import SecondaryCta from "components/SecondaryCta";
import {
  WithSourceTypeEvent,
  WithSourceTypeInnerProps,
} from "components/WithSourceTypeEventContext";
import { CompanySourceTypeParams, ConnectionParams, routes } from "config/urls";

import styles from "./CompanyError.module.css";

type CompanyErrorProps = RouteComponentProps<ConnectionParams> &
  WithSourceTypeInnerProps;

const CompanyErrorContent: React.FC<CompanyErrorProps> = ({
  match,
  sourceTypes,
  sourceTypesStatus,
  currentSourceType,
  nextPrioritySourceType,
}) => {
  const history = useHistory();
  const filteredSearch = useFilteredQueryString();
  const {
    state: { data: config, status: configStatus },
  } = useConfig();
  const { canContinue } = useContinueRedirectUrl(match.params.companyId);
  const [isLoadingCreateConnection, createConnection] =
    useCreateConnectionClickHandler({
      companyId: match.params.companyId,
      platformKey: match.params.platformKey,
      sourceType: currentSourceType?.sourceType,
    });

  const [{ loading: loadingDisplayText, data: displayText }] =
    useDisplayTextQuery({ companyId: match.params.companyId });

  const isComplete = useMemo(
    () =>
      sourceTypesStatus === SourceTypeStatus.READY &&
      configStatus === ConfigStatusType.READY &&
      !isLoadingCreateConnection &&
      !loadingDisplayText,
    [
      configStatus,
      sourceTypesStatus,
      isLoadingCreateConnection,
      loadingDisplayText,
    ]
  );

  const isMandatory =
    currentSourceType && !isOptional(currentSourceType.sourceType, config);

  const companyId = match.params.companyId;
  const canRetry = !!match.params["0"]; // Matches to the optional retry path param

  usePageViewedEvent({
    ready: !!currentSourceType?.sourceType && isComplete,
    clientConfig: config,
    sourceTypes,
  });

  const displayProps = useSourceTypeDisplayProps(currentSourceType);

  const nextPrioritySourceTypeRedirect = useNextSourceTypeRedirectHandler(
    companyId,
    nextPrioritySourceType
  );

  const overviewPageUrl = formatUrl(routes.company.overview, {
    companyId: match.params.companyId,
  });

  const handleNextButtonClick = useMemo(() => {
    if (isMandatory && currentSourceType?.connected === 0) {
      return undefined;
    }

    if (canContinue && nextPrioritySourceType) {
      return nextPrioritySourceTypeRedirect;
    }

    return () =>
      history.push({
        pathname: overviewPageUrl,
        search: filteredSearch,
      });
  }, [
    isMandatory,
    currentSourceType?.connected,
    canContinue,
    nextPrioritySourceType,
    nextPrioritySourceTypeRedirect,
    history,
    overviewPageUrl,
    filteredSearch,
  ]);

  const handleRetryButtonClick = () => {
    if (
      currentSourceType?.sourceType === "Banking" &&
      currentSourceType.useCustomIntro
    ) {
      createConnection();
    } else {
      history.push({
        pathname: formatUrl<CompanySourceTypeParams>(routes.company.link, {
          companyId,
          sourceType: match.params.sourceType?.toLowerCase(),
        }),
        search: filteredSearch,
      });
    }
  };

  return (
    <LinkPageRoot
      showLinkedAccounts
      isComplete={isComplete}
      currentSourceType={currentSourceType}
    >
      <ConnectionComplete
        Icon={(p: { className: string }) => (
          <CircleAlertIcon className={classNames(p.className, styles.icon)} />
        )}
        title="Connection Error"
      >
        <Typography variant="p" as="p" className={styles.errorMessage}>
          {displayText?.errorMessage ||
            "There has been an error connecting your account"}
        </Typography>

        {displayText?.correlationId && (
          <Typography variant="small" as="p" className={styles.correlationId}>
            Correlation ID: {displayText.correlationId}
          </Typography>
        )}
        {canRetry && (
          <SecondaryCta
            className={styles.secondaryCta}
            onClick={handleRetryButtonClick}
            label={displayProps.error.tryAgainMessage}
            data-testid="company-error-retry"
            analyticsName="Retry"
            eventProperties={{
              sourceType: currentSourceType?.sourceType,
            }}
          />
        )}
        <div className={styles.errorFooter}>
          {handleNextButtonClick && (
            <Button
              label="Next"
              analyticsName="Next"
              onClick={handleNextButtonClick}
              data-testid="primary-next-btn"
            />
          )}
        </div>
      </ConnectionComplete>
    </LinkPageRoot>
  );
};

export const CompanyError: React.FC<RouteComponentProps<ConnectionParams>> = (
  props
) => {
  return (
    <WithSourceTypeEvent
      sourceType={props.match.params?.sourceType}
      eventName={(sourceType) => `Link ${sourceType} error page`}
    >
      {(sourceTypeProps) => (
        <CompanyErrorContent {...sourceTypeProps} {...props} />
      )}
    </WithSourceTypeEvent>
  );
};
